import { FC, useState, useEffect, useMemo } from 'react'
import { FaBullseye, FaWrench, FaCrosshairs, FaCheck } from 'react-icons/fa'
import { MdOutlineKeyboardBackspace } from 'react-icons/md'
import { TabType } from '../BetLotto.types'
import TabBar from '../../TabBar'
import { TabItem } from '../../TabBar/TabBar.types'
import { useNavigate } from 'react-router'
import { useThemeFeatures } from '../../../hooks/useThemeFeatures'
import { useTranslation } from 'react-i18next'

export interface MainTabsProps {
  onTabChange: (tab: TabType) => void
  isResulted: boolean
  isYeekee: boolean
  isGameActive: boolean
  isEncrypt: boolean
}

const tabs: TabItem[] = [
  {
    label: 'lottoPage.navbar.lotto',
    icon: <FaBullseye />,
    value: 'bet',
  },
  {
    label: 'lottoPage.navbar.shootNumber',
    icon: <FaCrosshairs />,
    value: 'shoot',
  },
  {
    label: 'betResult.result',
    icon: <FaCheck />,
    value: 'result',
  },
  {
    label: 'lottoPage.navbar.rule',
    icon: <FaWrench />,
    value: 'rules',
  },
]

export const MainTabs: FC<MainTabsProps> = ({
  onTabChange,
  isYeekee = false,
  isEncrypt = false,
  isResulted = false,
  isGameActive = true,
}) => {
  const navigate = useNavigate()
  const themeFeatures = useThemeFeatures()
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState<TabType>('bet')
  // Filter tabs based on game type and result status using simplified boolean logic
  const visibleTabs = useMemo(
    () =>
      tabs.filter((tab) => {
        // Define which tabs should be visible based on conditions
        const isBetVisible = isGameActive
        const isShootVisible = isYeekee // Remark: Shoot tab act as Result for Yeekee too
        const isResultVisible = isEncrypt && isResulted // Result tab is for Yeekee Malta only
        const isRulesVisible = true // Rules are always visible

        return (
          (tab.value === 'bet' && isBetVisible) ||
          (tab.value === 'shoot' && isShootVisible) ||
          (tab.value === 'result' && isResultVisible) ||
          (tab.value === 'rules' && isRulesVisible)
        )
      }),
    [isGameActive, isYeekee, isEncrypt, isResulted],
  )

  const handleTabChange = (value: string) => {
    setActiveTab(value as TabType)
    onTabChange(value as TabType)
  }

  // When visibleTabs changed, set active tab to first tab
  useEffect(() => {
    if (visibleTabs.length > 0) {
      setActiveTab(visibleTabs[0].value as TabType)
      onTabChange(visibleTabs[0].value as TabType)
    }
  }, [visibleTabs, onTabChange])

  return (
    <div className="flex flex-col gap-2 md:flex-row md:items-center w-full mb-4 md:gap-4">
      <div
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
      >
        <MdOutlineKeyboardBackspace className="text-xl text-[var(--heading-text)]" />
        {themeFeatures.showBackLabel && (
          <span className="text-[var(--heading-text)] font-medium">
            {t('common.back')}
          </span>
        )}
      </div>
      <div className="flex-1 flex justify-center">
        <TabBar
          tabs={visibleTabs}
          activeTab={activeTab}
          onChange={handleTabChange}
          variant="default"
          size="md"
          fullWidth={false}
        />
      </div>
    </div>
  )
}
